export const DialogQueuePriority = {
  Security: 100,
  BlockingBilling: 90,
  CheckoutReturn: 80,
  OwnerActivation: 70,
  AdministrationSetup: 60,
  GrowthCta: 50,
  ProductFlow: 40,
  Marketing: 30,
} as const;

export interface DialogQueueOptions {
  /**
   * Higher priority requests are shown first. Equal priorities keep insertion order.
   */
  priority?: number;
  /**
   * Prevents duplicate pending requests for the same logical dialog.
   */
  dedupeKey?: string;
  /**
   * Re-checked right before the dialog is shown. Return false to drop stale requests.
   */
  guard?: () => boolean;
  /**
   * Runs once the dialog show method has been invoked.
   */
  onShown?: () => void;
  /**
   * Runs when the request is skipped because its guard failed or the dialog unmounted.
   */
  onSkipped?: () => void;
}

type QueuedShowRunner = (markShown: () => void) => Promise<unknown>;

interface QueuedDialogRequest {
  token: symbol;
  id: string;
  order: number;
  priority: number;
  dedupeKey?: string;
  guard?: () => boolean;
  onShown?: () => void;
  onSkipped?: () => void;
  run: QueuedShowRunner;
  promise: Promise<unknown>;
  resolve: (value: unknown) => void;
  reject: (reason?: unknown) => void;
}

let blockingDialogCount = 0;
let activeQueuedToken: symbol | null = null;
let activeQueuedDedupeKey: string | null = null;
let isDraining = false;
let isDrainScheduled = false;
let insertionCounter = 0;

const queuedRequests: QueuedDialogRequest[] = [];

const openDialogStack: HTMLElement[] = [];
const dialogStackListeners = new Set<() => void>();

const notifyDialogStackChange = (): void => {
  dialogStackListeners.forEach((listener) => {
    try {
      listener();
    } catch {
      // a listener failure must not break dialog open/close bookkeeping
    }
  });
};

const hasBlockingDialogs = (): boolean => blockingDialogCount > 0;

const clearActiveQueuedRequest = (): void => {
  activeQueuedToken = null;
  activeQueuedDedupeKey = null;
};

const sortQueuedRequests = (): void => {
  queuedRequests.sort((a, b) => b.priority - a.priority || a.order - b.order);
};

const skipRequest = (request: QueuedDialogRequest): void => {
  request.onSkipped?.();
  request.resolve(undefined);
};

const drainQueue = async (): Promise<void> => {
  if (isDraining || activeQueuedToken || hasBlockingDialogs()) return;

  isDraining = true;

  try {
    while (!activeQueuedToken && !hasBlockingDialogs() && queuedRequests.length > 0) {
      sortQueuedRequests();
      const request = queuedRequests.shift();
      if (!request) return;

      if (request.guard && !request.guard()) {
        skipRequest(request);
        continue;
      }

      activeQueuedToken = request.token;
      activeQueuedDedupeKey = request.dedupeKey ?? null;

      let hasMarkedShown = false;
      const markShown = (): void => {
        if (hasMarkedShown) return;
        hasMarkedShown = true;
        request.onShown?.();
      };

      try {
        const result = await request.run(markShown);
        request.resolve(result);
      } catch (error) {
        request.reject(error);
      }

      if (!hasBlockingDialogs()) {
        clearActiveQueuedRequest();
        continue;
      }

      break;
    }
  } finally {
    isDraining = false;
  }
};

const scheduleDrain = (): void => {
  if (isDrainScheduled) return;

  isDrainScheduled = true;
  queueMicrotask(() => {
    isDrainScheduled = false;
    void drainQueue();
  });
};

const findPendingRequestByDedupeKey = (dedupeKey: string): QueuedDialogRequest | undefined => {
  return queuedRequests.find((request) => request.dedupeKey === dedupeKey);
};

export const enqueueDialogShow = (request: {
  id: string;
  run: QueuedShowRunner;
  options?: DialogQueueOptions;
}): Promise<unknown> => {
  const options = request.options ?? {};

  if (options.dedupeKey) {
    const existingRequest = findPendingRequestByDedupeKey(options.dedupeKey);
    if (existingRequest) return existingRequest.promise;

    if (activeQueuedDedupeKey === options.dedupeKey) {
      return Promise.resolve(undefined);
    }
  }

  let resolveRequest: (value: unknown) => void = () => undefined;
  let rejectRequest: (reason?: unknown) => void = () => undefined;

  const promise = new Promise<unknown>((resolve, reject) => {
    resolveRequest = resolve;
    rejectRequest = reject;
  });

  queuedRequests.push({
    token: Symbol(request.id),
    id: request.id,
    order: insertionCounter++,
    priority: options.priority ?? 0,
    dedupeKey: options.dedupeKey,
    guard: options.guard,
    onShown: options.onShown,
    onSkipped: options.onSkipped,
    run: request.run,
    promise,
    resolve: resolveRequest,
    reject: rejectRequest,
  });

  scheduleDrain();

  return promise;
};

export const cancelQueuedDialogsForId = (id: string): void => {
  for (let index = queuedRequests.length - 1; index >= 0; index -= 1) {
    const request = queuedRequests[index];
    if (request.id !== id) continue;

    queuedRequests.splice(index, 1);
    skipRequest(request);
  }
};

export const registerBlockingDialogOpen = (dialogElement?: HTMLElement): (() => void) => {
  blockingDialogCount += 1;

  if (dialogElement) {
    openDialogStack.push(dialogElement);
    notifyDialogStackChange();
  }

  let hasReleased = false;

  return () => {
    if (hasReleased) return;

    hasReleased = true;
    blockingDialogCount = Math.max(0, blockingDialogCount - 1);

    if (dialogElement) {
      const index = openDialogStack.lastIndexOf(dialogElement);
      if (index !== -1) openDialogStack.splice(index, 1);
      notifyDialogStackChange();
    }

    if (!hasBlockingDialogs()) {
      clearActiveQueuedRequest();
      scheduleDrain();
    }
  };
};

/**
 * The element of the topmost open blocking dialog, or null when none are open.
 * Reflects native top-layer order (last opened is topmost).
 */
export const getTopmostDialogElement = (): HTMLElement | null => openDialogStack.at(-1) ?? null;

/**
 * Subscribe to open-dialog stack changes (a dialog opened or closed). Returns an unsubscribe fn.
 */
export const onDialogStackChange = (listener: () => void): (() => void) => {
  dialogStackListeners.add(listener);
  return () => {
    dialogStackListeners.delete(listener);
  };
};

export const getDialogQueueSnapshot = (): {
  activeQueuedDedupeKey: string | null;
  blockingDialogCount: number;
  queuedRequestCount: number;
} => ({
  activeQueuedDedupeKey,
  blockingDialogCount,
  queuedRequestCount: queuedRequests.length,
});

export const resetDialogQueueForTests = (): void => {
  queuedRequests.splice(0);
  blockingDialogCount = 0;
  activeQueuedToken = null;
  activeQueuedDedupeKey = null;
  isDraining = false;
  isDrainScheduled = false;
  insertionCounter = 0;
  openDialogStack.splice(0);
  dialogStackListeners.clear();
};
